import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { getApiUrl } from '../config/api';

const LeaseView = () => {
  const { leaseId }      = useParams();
  const navigate         = useNavigate();
  const containerRef     = useRef(null);

  const [lease, setLease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [signingInProgress, setSigningInProgress] = useState(false);

  useEffect(() => {
    // ---- 1. fetch lease details -------------------------------------------
    (async () => {
      try {
        const user_id = localStorage.getItem('user_id');
        const res = await fetch(
          getApiUrl(`/api/v1/lease/list/${leaseId}?user_id=${user_id}`)
        );
        if (!res.ok) throw new Error(`API ${res.status}`);
        setLease(await res.json());      // returns { url: "...embedded_signing_url..." }
      } catch (err) {
        setError(err.message);
      } finally { setLoading(false); }
    })();
  }, [leaseId]);

  // ---- 2. load SignWell + inject viewer ----------------------------------
  useEffect(() => {
    if (!lease || !containerRef.current) return;

    // Only try to load SignWell if we have a URL to display
    if (!lease.url) {
      console.warn('No URL available for this lease');
      return;
    }

    // Install global protection for accessing contentWindow on removed iframes
    // SignWell's code does: iframeEl.contentWindow.postMessage(...)
    // We need to prevent the error when contentWindow is null
    // ...basically signwell's API doesn't play nice with SPA apps.
    
    // Only install the protection if it hasn't been installed yet
    if (!window.__signwellContentWindowProtectionInstalled) {
      // Store the original contentWindow getter
      const iframeProto = window.HTMLIFrameElement.prototype;
      const originalContentWindowGetter = Object.getOwnPropertyDescriptor(iframeProto, 'contentWindow').get;
      
      // Store for potential future use
      window.__signwellOriginalContentWindowGetter = originalContentWindowGetter;
      
      // Create a safe wrapper that returns a proxy when contentWindow is null
      Object.defineProperty(iframeProto, 'contentWindow', {
        get: function() {
          const realContentWindow = originalContentWindowGetter.call(this);
          
          if (realContentWindow === null || realContentWindow === undefined) {
            // Return a proxy that has a no-op postMessage (silently handle the error)
            return {
              postMessage: function() {
                // Silently ignore - this is expected when navigating away from LeaseView
              },
              location: {},
              document: {}
            };
          }
          
          return realContentWindow;
        },
        configurable: true
      });
      
      window.__signwellContentWindowProtectionInstalled = true;
    }

    let frameInstance = null;
    let retryCount = 0;
    const maxRetries = 3;

    // Function to initialize the SignWell frame
    function openFrame() {
      try {
        // Make sure the container element exists before creating the frame
        const container = document.getElementById('signwell-container');
        if (!container) {
          console.error('SignWell container not found');
          return;
        }

        // Wait a bit longer to ensure everything is ready
        setTimeout(() => {
          try {
            // eslint-disable-next-line no-undef
            frameInstance = new SignWellEmbed({
              url: lease.url,
              containerId: 'signwell-container',
              showHeader: true,
              allowDownload: true,
              events: {
                completed: () => console.log('Lease signed!'),
                closed: () => console.log('Signer closed viewer'),
                error: (err) => {
                  // Handle specific error for signing in progress without logging as error
                  if (err.errorCode === 'signing_in_progress') {
                    setSigningInProgress(true);
                    console.log('Another party is currently signing this document');
                    return false; // Signal that we've handled this error
                  }

                  // Only log as error for other error types
                  console.error('SignWell error:', err);

                  // Retry logic for other errors
                  if (retryCount < maxRetries) {
                    retryCount++;
                    console.log(`Retrying SignWell initialization (${retryCount}/${maxRetries})...`);
                    setTimeout(openFrame, 500 * retryCount); // Exponential backoff
                  }
                  return true; // Let other errors propagate
                }
              }
            });

            // Longer delay before opening to ensure DOM and iframe are ready
            setTimeout(() => {
              try {
                frameInstance.open();
              } catch (openError) {
                console.error('Error opening SignWell frame:', openError);
              }
            }, 200);
          } catch (initError) {
            console.error('Error creating SignWell instance:', initError);
            // Check if this is a signing in progress error
            if (initError?.errorCode === 'signing_in_progress') {
              setSigningInProgress(true);
              return; // Don't retry for this specific error
            }
          }
        }, 100);
      } catch (error) {
        console.error('Error in openFrame function:', error);
      }
    }

    // Clean up function to handle component unmounting
    const cleanup = () => {
      // Close the frame instance properly
      if (frameInstance) {
        try {
          if (typeof frameInstance.close === 'function') {
            frameInstance.close();
          }
          frameInstance = null;
        } catch (err) {
          // Silently handle errors
        }
      }

      // Function to remove iframes
      const removeIframes = () => {
        const allIframes = document.querySelectorAll('iframe');
        
        allIframes.forEach((iframe) => {
          // Check if this is a SignWell iframe (case-insensitive)
          const isSrc = iframe.src && iframe.src.toLowerCase().includes('signwell');
          const isId = iframe.id && iframe.id.toLowerCase().includes('signwell');
          const isInContainer = containerRef.current && containerRef.current.contains(iframe);
          
          if (isSrc || isId || isInContainer) {
            try {
              // Forcefully set src to about:blank before removing to stop any pending operations
              iframe.src = 'about:blank';
              
              // Remove from DOM
              if (iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
              }
            } catch (removeErr) {
              // Silently handle errors
            }
          }
        });
      };

      // Remove iframes immediately
      try {
        removeIframes();
      } catch (err) {
        // Silently handle errors
      }

      // Also remove iframes after a short delay (in case they're created asynchronously)
      setTimeout(() => {
        try {
          removeIframes();
        } catch (err) {
          // Silently handle errors
        }
      }, 100);

      // And one more time after a longer delay
      setTimeout(() => {
        try {
          removeIframes();
        } catch (err) {
          // Silently handle errors
        }
      }, 500);

      // Clear the container
      try {
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
        const container = document.getElementById('signwell-container');
        if (container) {
          container.innerHTML = '';
        }
      } catch (err) {
        // Silently handle errors
      }

      // Remove the SignWell script from the DOM
      try {
        const signwellScripts = document.querySelectorAll('script[src*="signwell.com"]');
        signwellScripts.forEach(script => {
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        });
      } catch (err) {
        // Silently handle errors
      }

      // Remove the global SignWell object and any related globals
      try {
        if (window.SignWellEmbed) {
          delete window.SignWellEmbed;
        }
        // Also remove any other SignWell globals (but keep our protection flags)
        const signwellKeys = Object.keys(window).filter(key => 
          key.toLowerCase().includes('signwell') && 
          !key.startsWith('__signwell') // Keep our internal protection markers
        );
        signwellKeys.forEach(key => {
          try {
            delete window[key];
          } catch (e) {
            // Some properties may not be deleteable
          }
        });
      } catch (err) {
        // Silently handle errors
      }

      // Keep the contentWindow protection in place - it's a safe wrapper that prevents all future errors
    };

    // lazy-inject the SignWell script only once
    if (!window.SignWellEmbed) {
      const s = document.createElement('script');
      s.src = 'https://static.signwell.com/assets/embedded.js';
      s.async = true;
      s.onload = () => {
        // Add a small delay after script loads before initializing
        setTimeout(openFrame, 200);
      };
      s.onerror = () => console.error('Failed to load SignWell script');
      document.body.appendChild(s);
    } else {
      openFrame();
    }

    // Clean up when component unmounts
    return cleanup;
  }, [lease]);

  // ---- 3. render ----------------------------------------------------------
  if (loading) return <Layout><LoadingSpinner /></Layout>;
  if (error)   return <Layout><p className="text-red-600">{error}</p></Layout>;

  return (
    <Layout pageTitle="Lease Details">
      <div className="flex flex-col w-full">
        {/* Lease title header */}
        <h2 className="text-2xl font-semibold mb-4">Lease {leaseId}</h2>

        {/* SignWell container - main focus of the page */}
        <div className="w-full flex-grow mb-6">
          {signingInProgress ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 text-center">
              <h3 className="text-xl font-medium text-yellow-700 mb-2">Signing in Progress</h3>
              <p className="text-yellow-600">
                Another party is currently signing this document. Please try again later.
              </p>
            </div>
          ) : (
            lease.url && (
              <div
                id="signwell-container"
                ref={containerRef}
                className="w-full h-[900px] rounded-2xl overflow-hidden shadow-md"
              />
            )
          )}
        </div>

        {/* Back button at the bottom */}
        <div className="w-full mt-4 mb-6">
          <Button onClick={() => navigate(-1)}>Back to list</Button>
        </div>
      </div>
    </Layout>
  );
};

export default LeaseView;
